import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import type { ScreenSettings, SepMode } from '@eip/engine';
import { Select, Slider, Toggle, clampNumber, commitDraft, nextDraft } from './controls';
import { applyMeshPreset, meshOptions, toggleDetail } from '../lib/sep-ui';
import { Section } from './Section';

const SHAPES = ['round', 'ellipse', 'line', 'square', 'diamond'].map((s) => ({ value: s, label: s }));
const FILM_DPI_OPTIONS = [600, 720, 1200].map((d) => ({ value: String(d), label: `${d} dpi` }));
const ANGLE_MODES = [{ value: 'single', label: 'Single angle' }, { value: 'cmyk', label: 'CMYK angles' }];
/* The same one-liner the halftone step's LPI slider carries, spelled once: this
   row is hand-rolled (a free-typed number with its own draft state), so it gets
   the tooltip `Slider`'s `hint` would have given it. */
const LPI_HINT = 'Halftone dot lines per inch';

export function ScreenPanel({ screen, mode, onChange }: { screen: ScreenSettings; mode: SepMode; onChange: (next: ScreenSettings) => void }) {
  const set = (patch: Partial<ScreenSettings>) => onChange({ ...screen, ...patch });

  // A persisted or hand-set filmDpi outside 600/720/1200 must still show as
  // itself, not silently snap the select to one of the three presets.
  const filmDpiValue = String(screen.filmDpi);
  const filmDpiOptions = FILM_DPI_OPTIONS.some((o) => o.value === filmDpiValue)
    ? FILM_DPI_OPTIONS
    : [...FILM_DPI_OPTIONS, { value: filmDpiValue, label: `${screen.filmDpi} dpi (custom)` }].sort((a, b) => Number(a.value) - Number(b.value));

  // The mesh preset's "detail" (÷4 vs ÷5) toggle isn't part of ScreenSettings —
  // it only matters at the moment a preset is (re-)applied — so it lives here
  // as local UI state rather than on the separation itself.
  const [detail, setDetail] = useState(false);
  const applyMesh = (mesh: number, det: boolean) => onChange(applyMeshPreset(screen, mesh, det));

  // LPI and index dpi are free-typed numbers (see SettingsBar's width/dpi
  // fields): a local draft so every keystroke shows, committed on blur/Enter,
  // and skipped while focused so an external change (e.g. picking a mesh
  // preset) can't clobber an in-progress edit.
  const [lpiDraft, setLpiDraft] = useState(String(screen.lpi));
  const lpiFocused = useRef(false);
  const lpiDirty = useRef(false);
  useEffect(() => { setLpiDraft((prev) => nextDraft(prev, String(screen.lpi), lpiFocused.current)); }, [screen.lpi]);
  const commitLpi = (raw: string) => {
    const v = commitDraft(raw, screen.lpi, 10, 150, 1);
    setLpiDraft(String(v));
    if (v !== screen.lpi) set({ lpi: v });
  };

  const [dpiDraft, setDpiDraft] = useState(String(screen.indexDpi));
  const dpiFocused = useRef(false);
  const dpiDirty = useRef(false);
  useEffect(() => { setDpiDraft((prev) => nextDraft(prev, String(screen.indexDpi), dpiFocused.current)); }, [screen.indexDpi]);
  const commitIndexDpi = (raw: string) => {
    const v = commitDraft(raw, screen.indexDpi, 100, 400, 1);
    setDpiDraft(String(v));
    if (v !== screen.indexDpi) set({ indexDpi: v });
  };

  return (
    <Section id="screening" title="Screening">
      <Select label="Mesh preset" value={String(screen.mesh)} options={meshOptions()} onChange={(v) => applyMesh(Number(v), detail)} />
      {/* Toggling detail alone must not stomp a hand-typed LPI — `toggleDetail`
          only re-derives it when the current value still matches the preset
          it came from (see its doc comment). Picking a NEW mesh preset above
          is a deliberate choice and always overwrites lpi, so that keeps
          using `applyMesh`. */}
      <Toggle label="Detail (finer dots)" checked={detail} onChange={(v) => { setDetail(v); onChange(toggleDetail(screen, v)); }} />
      <label className="ctl" title={LPI_HINT}>
        <span className="ctl-label">LPI</span>
        <input
          type="number" className="ctl-num" min={10} max={150} value={lpiDraft} aria-label="LPI" title={LPI_HINT}
          onFocus={() => { lpiFocused.current = true; }}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setLpiDraft(e.target.value);
            lpiDirty.current = true;
            if ((e.nativeEvent as InputEvent).inputType === 'insertReplacementText') { commitLpi(e.target.value); lpiDirty.current = false; }
          }}
          onBlur={() => { lpiFocused.current = false; if (lpiDirty.current) { commitLpi(lpiDraft); lpiDirty.current = false; } }}
          onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') { commitLpi(lpiDraft); lpiDirty.current = false; }
            else if (e.key === 'Escape') { setLpiDraft(String(screen.lpi)); lpiDirty.current = false; }
          }}
        />
      </label>
      <Select label="Angle mode" value={screen.angleMode} options={ANGLE_MODES} onChange={(v) => set({ angleMode: v as ScreenSettings['angleMode'] })} />
      <Slider label="Angle" value={screen.angle} min={0} max={180} step={0.5} unit="°" onChange={(v) => set({ angle: v })} />
      <Select label="Dot shape" hint="Shape of the halftone dot" value={screen.shape} options={SHAPES} onChange={(v) => set({ shape: v as ScreenSettings['shape'] })} />
      <Select label="Film dpi" value={String(screen.filmDpi)} options={filmDpiOptions} onChange={(v) => set({ filmDpi: Number(v) })} />
      <Slider label="Dot gain" hint="Compensates for dots growing on press" value={Math.round(screen.gain * 100)} min={0} max={50} unit="%" onChange={(v) => set({ gain: v / 100 })} />
      <Slider label="Min dot" value={Math.round(screen.minDot * 100)} min={0} max={20} unit="%" onChange={(v) => set({ minDot: v / 100 })} />
      <Slider label="Max dot" value={Math.round(screen.maxDot * 100)} min={80} max={100} unit="%" onChange={(v) => set({ maxDot: v / 100 })} />
      <Toggle
        label="Custom underbase LPI"
        checked={screen.underbaseLpi !== undefined}
        onChange={(v) => set({ underbaseLpi: v ? screen.lpi : undefined })}
      />
      {screen.underbaseLpi !== undefined && (
        <Slider label="Underbase LPI" hint="Halftone dot lines per inch for the underbase" value={screen.underbaseLpi} min={10} max={150} step={1} onChange={(v) => set({ underbaseLpi: v })} />
      )}
      {mode === 'index' && (
        <label className="ctl">
          <span className="ctl-label">Index dpi</span>
          <input
            type="number" className="ctl-num" min={100} max={400} value={dpiDraft} aria-label="Index dpi"
            onFocus={() => { dpiFocused.current = true; }}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setDpiDraft(e.target.value);
              dpiDirty.current = true;
              if ((e.nativeEvent as InputEvent).inputType === 'insertReplacementText') { commitIndexDpi(e.target.value); dpiDirty.current = false; }
            }}
            onBlur={() => { dpiFocused.current = false; if (dpiDirty.current) { commitIndexDpi(dpiDraft); dpiDirty.current = false; } }}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') { commitIndexDpi(dpiDraft); dpiDirty.current = false; }
              else if (e.key === 'Escape') { setDpiDraft(String(screen.indexDpi)); dpiDirty.current = false; }
            }}
          />
        </label>
      )}
    </Section>
  );
}
